'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { fetchColleges, College } from '@/lib/services/collegeService';
import { fetchExams, Exam } from '@/lib/services/examService';
import PredictorResults from './PredictorResults';
import { Target, MapPin, BookOpen, ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export interface PredictorFormData {
  examId: string;
  score: string;
  scoreUnit: string;
  category: string;
  preferredState: string;
  preferredStream: string;
}

export interface PredictionResult {
  collegeId: string;
  collegeName: string;
  shortName: string;
  location: string;
  state: string;
  nirfRank: number | null;
  totalFees: number;
  cutoffExam: string;
  cutoffScore: number;
  cutoffUnit: string;
  accreditation: string;
  rating: number;
  streams: string[];
  probability: 'High' | 'Medium' | 'Low';
  probabilityScore: number;
  yourScore: number;
  cutoffGap: number;
  ownership: string;
  placementAvgPackage: number;
}

const steps = [
  { id: 1, label: 'Select Exam', icon: BookOpen },
  { id: 2, label: 'Enter Score', icon: Target },
  { id: 3, label: 'Preferences', icon: MapPin },
];

const categories = ['General', 'OBC', 'SC', 'ST', 'EWS'];
const states = ['Any State', 'Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Rajasthan', 'West Bengal', 'Punjab', 'Uttar Pradesh', 'Telangana', 'Kerala'];
const streams = ['Any Stream', 'Engineering', 'Medical', 'Management', 'Law', 'Arts', 'Commerce', 'Science'];

function computePredictions(data: PredictorFormData, colleges: College[], exams: Exam[]): PredictionResult[] {
  const selectedExam = exams.find((e) => e.id === data.examId);
  if (!selectedExam) return [];

  const userScore = parseFloat(data.score);
  if (isNaN(userScore)) return [];

  const results: PredictionResult[] = [];

  colleges.forEach((college) => {
    // Filter by stream preference
    if (data.preferredStream !== 'Any Stream' && !college.streams.includes(data.preferredStream as any)) return;
    // Filter by state preference
    if (data.preferredState !== 'Any State' && college.state !== data.preferredState) return;

    // Try to match cutoff data
    const cutoffEntry = college.cutoffs.find(
      (c) => c.exam.toLowerCase().includes(selectedExam.shortName.toLowerCase()) ||
             selectedExam.shortName.toLowerCase().includes(c.exam.toLowerCase().split(' ')[0])
    );

    let collegeCutoff: number;
    let cutoffUnit: string;

    if (cutoffEntry) {
      const catMap: Record<string, number> = {
        General: cutoffEntry.general,
        OBC: cutoffEntry.obc,
        SC: cutoffEntry.sc,
        ST: cutoffEntry.st,
        EWS: cutoffEntry.ews,
      };
      collegeCutoff = catMap[data.category] ?? cutoffEntry.general;
      cutoffUnit = cutoffEntry.unit;
    } else {
      // Use college's general cutoff as fallback
      collegeCutoff = college.cutoffScore;
      cutoffUnit = college.cutoffUnit;
    }

    // Determine probability based on score vs cutoff
    // For rank-based: lower rank = better, so user needs score <= cutoff
    // For percentile/marks: higher = better, user needs score >= cutoff
    let probability: 'High' | 'Medium' | 'Low';
    let probabilityScore: number;
    let cutoffGap: number;

    const isRankBased = cutoffUnit === 'rank';

    if (isRankBased) {
      // Lower rank number = better performance
      const ratio = collegeCutoff / userScore;
      cutoffGap = collegeCutoff - userScore;
      if (ratio >= 2) {
        probability = 'High';
        probabilityScore = 90;
      } else if (ratio >= 1.2) {
        probability = 'High';
        probabilityScore = 78;
      } else if (ratio >= 0.8) {
        probability = 'Medium';
        probabilityScore = 55;
      } else {
        probability = 'Low';
        probabilityScore = 25;
      }
    } else {
      // Higher score = better performance
      const diff = userScore - collegeCutoff;
      cutoffGap = diff;
      const pct = (diff / collegeCutoff) * 100;
      if (pct >= 5) {
        probability = 'High';
        probabilityScore = 88;
      } else if (pct >= 0) {
        probability = 'High';
        probabilityScore = 72;
      } else if (pct >= -5) {
        probability = 'Medium';
        probabilityScore = 48;
      } else {
        probability = 'Low';
        probabilityScore = 18;
      }
    }

    results.push({
      collegeId: college.id,
      collegeName: college.name,
      shortName: college.shortName,
      location: college.location,
      state: college.state,
      nirfRank: college.nirfRank,
      totalFees: college.totalFees,
      cutoffExam: college.cutoffExam,
      cutoffScore: collegeCutoff,
      cutoffUnit,
      accreditation: college.accreditation,
      rating: college.rating,
      streams: college.streams,
      probability,
      probabilityScore,
      yourScore: userScore,
      cutoffGap,
      ownership: college.ownership,
      placementAvgPackage: college.placementAvgPackage,
    });
  });

  // Sort: High first, then Medium, then Low; within same probability sort by NIRF rank
  const order: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
  return results.sort((a, b) => {
    if (order[a.probability] !== order[b.probability]) return order[a.probability] - order[b.probability];
    return (a.nirfRank ?? 999) - (b.nirfRank ?? 999);
  });
}

export default function PredictorContent() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<PredictionResult[] | null>(null);
  const [colleges, setColleges] = useState<College[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchColleges(), fetchExams()]).then(([collegeData, examData]) => {
      setColleges(collegeData);
      setExams(examData);
      setDataLoading(false);
    });
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<PredictorFormData>({
    defaultValues: {
      examId: '',
      score: '',
      scoreUnit: 'percentile',
      category: 'General',
      preferredState: 'Any State',
      preferredStream: 'Any Stream',
    },
  });

  const watchedExamId = watch('examId');
  const watchedScore = watch('score');
  const watchedCategory = watch('category');
  const selectedExam = exams.find((e) => e.id === watchedExamId);

  const scoreUnitOptions: Record<string, { value: string; label: string }[]> = {
    'exam-001': [{ value: 'percentile', label: 'Percentile (0–100)' }],
    'exam-002': [{ value: 'rank', label: 'AIR Rank' }],
    'exam-003': [{ value: 'marks', label: 'Marks (0–720)' }],
    'exam-004': [{ value: 'percentile', label: 'Percentile (0–100)' }],
    'exam-005': [{ value: 'score', label: 'Score (0–150)' }],
    'exam-006': [{ value: 'score', label: 'Score (0–1000)' }],
    'exam-007': [{ value: 'percentile', label: 'Percentile (0–100)' }],
    'exam-008': [{ value: 'percentile', label: 'Percentile (0–100)' }],
  };

  const scorePlaceholder: Record<string, string> = {
    'exam-001': 'e.g. 97.5 (percentile)',
    'exam-002': 'e.g. 1200 (AIR rank)',
    'exam-003': 'e.g. 680 (out of 720)',
    'exam-004': 'e.g. 98 (percentile)',
    'exam-005': 'e.g. 105 (out of 150)',
    'exam-006': 'e.g. 750 (out of 1000)',
    'exam-007': 'e.g. 94 (percentile)',
    'exam-008': 'e.g. 92 (percentile)',
  };

  const canGoNext = () => {
    if (currentStep === 1) return !!watchedExamId;
    if (currentStep === 2) return !!watchedScore && !errors.score;
    return true;
  };

  const onSubmit = async (data: PredictorFormData) => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      return;
    }
    setIsLoading(true);
    // Backend integration point: POST /api/predictor with form data
    await new Promise((r) => setTimeout(r, 1800));
    const predictions = computePredictions(data, colleges, exams);
    setResults(predictions);
    setIsLoading(false);
    if (predictions.length === 0) {
      toast.error('No colleges matched your preferences. Try broadening your filters.');
    } else {
      toast.success(`Found ${predictions.length} colleges matching your profile!`);
    }
  };

  const handleReset = () => {
    reset();
    setResults(null);
    setCurrentStep(1);
  };

  if (results !== null) {
    return (
      <PredictorResults
        results={results}
        formData={{ exam: selectedExam?.shortName ?? '', category: watchedCategory, score: watchedScore }}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, i) => (
          <React.Fragment key={`step-indicator-${step.id}`}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <step.icon size={18} />
                )}
              </div>
              <span
                className={`text-xs font-semibold whitespace-nowrap ${
                  currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`h-0.5 flex-1 mx-3 mb-5 transition-colors ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-border'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form card */}
      <div className="card-base p-6 sm:p-8">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1: Select Exam */}
          {currentStep === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-1">Which exam did you appear for?</h2>
              <p className="text-sm text-muted-foreground mb-6">Select your entrance exam to get relevant college predictions.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {exams.map((exam) => {
                  const streamBadgeClass =
                    exam.stream[0] === 'Engineering' ? 'stream-badge-engineering' :
                    exam.stream[0] === 'Medical' ? 'stream-badge-medical' :
                    exam.stream[0] === 'Management' ? 'stream-badge-management' :
                    exam.stream[0] === 'Law'? 'stream-badge-law' : 'stream-badge-arts';

                  return (
                    <label
                      key={`exam-select-${exam.id}`}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        watchedExamId === exam.id
                          ? 'border-primary bg-accent/40' :'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                    >
                      <input
                        type="radio"
                        value={exam.id}
                        {...register('examId', { required: 'Please select an exam' })}
                        className="mt-0.5 accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-foreground text-sm">{exam.shortName}</span>
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${streamBadgeClass}`}>
                            {exam.stream[0]}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-tight truncate">{exam.conductingBody}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              {errors.examId && (
                <p className="text-xs text-danger flex items-center gap-1.5 mt-3">
                  <AlertCircle size={13} />
                  {errors.examId.message}
                </p>
              )}
            </div>
          )}

          {/* Step 2: Enter Score */}
          {currentStep === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-1">
                Enter your {selectedExam?.shortName} score
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your score will be compared against 2025 cutoff data to predict admission chances.
              </p>

              {/* Selected exam info */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-6">
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  <BookOpen size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-bold text-foreground text-sm">{selectedExam?.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedExam?.conductingBody}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="ml-auto text-xs text-primary font-semibold hover:underline"
                >
                  Change
                </button>
              </div>

              <div className="space-y-5">
                {/* Score unit */}
                {watchedExamId && scoreUnitOptions[watchedExamId] && (
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">
                      Score Type
                    </label>
                    <div className="flex gap-3">
                      {scoreUnitOptions[watchedExamId].map((opt) => (
                        <label key={`score-unit-${opt.value}`} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value={opt.value}
                            {...register('scoreUnit')}
                            defaultChecked
                            className="accent-primary"
                          />
                          <span className="text-sm text-foreground">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Score input */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Your Score / Rank <span className="text-danger">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Enter the score from your official result. Do not enter estimated scores.
                  </p>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={watchedExamId ? scorePlaceholder[watchedExamId] : 'Enter your score'}
                    {...register('score', {
                      required: 'Score is required',
                      min: { value: 0, message: 'Score cannot be negative' },
                      validate: (v) => !isNaN(parseFloat(v)) || 'Please enter a valid number',
                    })}
                    className="input-base text-base font-semibold"
                  />
                  {errors.score && (
                    <p className="text-xs text-danger flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} />
                      {errors.score.message}
                    </p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Category <span className="text-danger">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Your reservation category determines which cutoff is used for comparison.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <label
                        key={`cat-${cat}`}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all text-sm font-semibold ${
                          watchedCategory === cat
                            ? 'border-primary bg-accent/40 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <input
                          type="radio"
                          value={cat}
                          {...register('category', { required: true })}
                          className="sr-only"
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preferences */}
          {currentStep === 3 && (
            <div className="animate-fade-in">
              <h2 className="text-xl font-bold text-foreground mb-1">
                Set your preferences
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Narrow results by location and stream. You can keep both as &quot;Any&quot; to see all colleges.
              </p>

              {/* Summary card */}
              <div className="bg-muted rounded-xl p-4 mb-6">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Your Profile Summary</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Exam</p>
                    <p className="font-bold text-foreground text-sm">{selectedExam?.shortName}</p>
                  </div>
                  <div className="border-x border-border">
                    <p className="text-xs text-muted-foreground mb-0.5">Score</p>
                    <p className="font-bold text-primary text-sm font-tabular">{watchedScore}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Category</p>
                    <p className="font-bold text-foreground text-sm">{watchedCategory}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Preferred State / Location
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select a state to see colleges in that region, or keep as &quot;Any State&quot; for all India results.
                  </p>
                  <div className="relative">
                    <select
                      {...register('preferredState')}
                      className="select-base pr-10"
                    >
                      {states.map((state) => (
                        <option key={`state-pref-${state}`} value={state}>{state}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">▾</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Preferred Stream / Course
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Filter by your target stream to see only relevant colleges.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {streams.map((stream) => {
                      const watched = watch('preferredStream');
                      return (
                        <label
                          key={`stream-pref-${stream}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all text-sm font-semibold ${
                            watched === stream
                              ? 'border-primary bg-accent/40 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                          }`}
                        >
                          <input
                            type="radio"
                            value={stream}
                            {...register('preferredStream')}
                            className="sr-only"
                          />
                          {stream}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-border">
            <button
              type="button"
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : undefined}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={16} />
              Back
            </button>

            <div className="flex items-center gap-2">
              {steps.map((step) => (
                <span
                  key={`step-dot-${step.id}`}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentStep === step.id ? 'bg-primary w-5' : currentStep > step.id ? 'bg-green-500' : 'bg-border'
                  }`}
                />
              ))}
            </div>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={() => canGoNext() && setCurrentStep(currentStep + 1)}
                disabled={!canGoNext()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex items-center gap-2 min-w-[160px] justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Predicting...
                  </>
                ) : (
                  <>
                    <Target size={16} />
                    Predict My Colleges
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Trust badges */}
      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        {[
          { label: '2025 Cutoff Data', desc: 'Official counselling data' },
          { label: '15+ Colleges', desc: 'Across all major streams' },
          { label: 'Category-wise', desc: 'General, OBC, SC, ST, EWS' },
        ].map((badge) => (
          <div key={`trust-${badge.label}`} className="card-base p-3">
            <CheckCircle2 size={16} className="text-green-500 mx-auto mb-1" />
            <p className="text-xs font-bold text-foreground">{badge.label}</p>
            <p className="text-xs text-muted-foreground">{badge.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}